import React, { FC, ReactElement } from "react";

import { Alert, Divider, Stack, Typography } from "@mui/material";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";

import MultiValueTextFieldGroup from "../../../../components/MultiValueTextFieldGroup";
import SettingSection from "../../../../components/SettingSection";
import { setSettingsFormProperty } from "../../../../slices/settings";
import { useDispatch, useSelector } from "../../../../store";
import { SectionProps } from "../../../routes/components/edit_sections/common";

const Mcp: FC<SectionProps> = ({
  readonly,
  tabValue,
  refresh,
}): ReactElement => {
  const dispatch = useDispatch();
  const mcpAllowedClientIdDomains = useSelector(
    (state) => state.settings.form.mcpAllowedClientIdDomains,
  );
  const mcpAllowedAsMetadataDomains = useSelector(
    (state) => state.settings.form.mcpAllowedAsMetadataDomains,
  );

  return (
    <Container component={Paper} maxWidth={false} disableGutters={true}>
      <Stack key={tabValue + refresh} spacing={1} sx={{ m: 3 }}>
        <Typography id={tabValue} variant="h2" sx={{ pt: 2 }}>
          MCP
        </Typography>
        <Divider />

        <Alert severity="info" sx={{ my: 1 }}>
          These settings control domain allowlists for{" "}
          <strong>Model Context Protocol (MCP)</strong> OAuth metadata
          discovery. <strong>Allowed Client ID Domains</strong> restricts which
          domains may serve Client ID Metadata Documents when MCP clients
          authenticate with URL-based client IDs (per{" "}
          <a
            href="https://datatracker.ietf.org/doc/draft-ietf-oauth-client-id-metadata-document/"
            target="_blank"
            rel="noopener noreferrer"
          >
            draft-ietf-oauth-client-id-metadata-document
          </a>
          ). <strong>Allowed AS Metadata Domains</strong> restricts which
          upstream domains Pomerium may contact to fetch OAuth Authorization
          Server and Protected Resource Metadata (RFC 8414 / RFC 9728) when
          proxying to upstream MCP servers. Both settings prevent SSRF by
          ensuring Pomerium only fetches metadata from explicitly trusted
          domains. Supports wildcard patterns (e.g., *.example.com).
        </Alert>

        <SettingSection
          label="Allowed Client ID Domains"
          docsKey="mcp-allowed-client-id-domains"
        >
          <MultiValueTextFieldGroup
            disabled={readonly}
            values={mcpAllowedClientIdDomains || []}
            onSave={(values) =>
              dispatch(
                setSettingsFormProperty({
                  path: "mcpAllowedClientIdDomains",
                  value: values,
                }),
              )
            }
          />
        </SettingSection>
        <Divider />

        <SettingSection
          label="Allowed As Metadata Domains"
          docsKey="mcp-allowed-as-metadata-domains"
        >
          <MultiValueTextFieldGroup
            disabled={readonly}
            values={mcpAllowedAsMetadataDomains || []}
            onSave={(values) =>
              dispatch(
                setSettingsFormProperty({
                  path: "mcpAllowedAsMetadataDomains",
                  value: values,
                }),
              )
            }
          />
        </SettingSection>
      </Stack>
    </Container>
  );
};

export default Mcp;
